import type { Lot } from '../../types/lot'

type LineageCompatibleLot = Pick<
  Lot,
  'id' | 'lotKey' | 'lineageRootId' | 'sourceLotKey' | 'farmId' | 'productionUnitId' | 'locationType' | 'locationId' | 'species'
>

export type DerivedLotCompatibilityDraft = Pick<
  Lot,
  'lotKey' | 'lineageRootId' | 'sourceLotKey' | 'farmId' | 'productionUnitId' | 'locationType' | 'locationId' | 'species'
>

const normalizeOptionalString = (value: string | undefined): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const resolveLineageRootId = (lot: Pick<LineageCompatibleLot, 'id' | 'lineageRootId'>): string | undefined => (
  normalizeOptionalString(lot.lineageRootId) ?? normalizeOptionalString(lot.id)
)

const resolveSourceLotKey = (lot: Pick<LineageCompatibleLot, 'lotKey' | 'sourceLotKey'>): string | undefined => (
  normalizeOptionalString(lot.sourceLotKey) ?? normalizeOptionalString(lot.lotKey)
)

const resolveSharedValue = (
  lots: LineageCompatibleLot[],
  resolver: (lot: LineageCompatibleLot) => string | undefined
): string | undefined => {
  let sharedValue: string | undefined

  for (const lot of lots) {
    const nextValue = resolver(lot)
    if (!nextValue) return undefined

    if (sharedValue === undefined) {
      sharedValue = nextValue
      continue
    }

    if (sharedValue !== nextValue) {
      return undefined
    }
  }

  return sharedValue
}

export const buildSplitTargetLotDraft = (
  source: LineageCompatibleLot,
  lotKey: string
): DerivedLotCompatibilityDraft => ({
  lotKey,
  lineageRootId: resolveLineageRootId(source),
  sourceLotKey: resolveSourceLotKey(source),
  farmId: source.farmId,
  productionUnitId: source.productionUnitId,
  locationType: source.locationType,
  locationId: source.locationId,
  species: source.species
})

export const buildMergeTargetLotDraft = (
  sources: LineageCompatibleLot[],
  lotKey: string
): DerivedLotCompatibilityDraft => {
  const primary = sources[0]

  return {
    lotKey,
    lineageRootId: resolveSharedValue(sources, resolveLineageRootId),
    sourceLotKey: resolveSharedValue(sources, resolveSourceLotKey),
    farmId: primary.farmId,
    productionUnitId: primary.productionUnitId,
    locationType: primary.locationType,
    locationId: primary.locationId,
    species: primary.species
  }
}